import BaseMedia, { BaseMediaEvents } from './base';
import SlideshowMedia from './slideshow';
import HLS from 'hls.js/dist/hls.light';
import kebabCase from 'lodash/kebabCase';
import last from 'lodash/last';
import { HTML5Adapter } from 'player.js';
import { isMobile } from '../device';

export const MediaEvents = {
  ...BaseMediaEvents,

  Buffered: 'buffered',

  Change_Level: 'change:level',
};

export enum MediaType {
  Audio = 'audio',
  Video = 'video',
  Slideshow = 'slideshow',
}

export type MediaOptions = {
  webkitPlaysinline?: string;
  playsinline?: string;
  preload?: 'auto';
  crossorigin?: 'anonymous';
  controls?: 'controls';
};

const cacheRoot = document.querySelector('#app-cache');

class Media extends BaseMedia {
  protected static resourceList: { [key: string]: Media | undefined } = {};
  private type: MediaType;
  private adapter: any;
  public hls: HLS;
  public element: HTMLAudioElement | HTMLVideoElement | HTMLMediaElement;
  private ready: boolean;
  private currentSrc?: string;
  private customLevels?: HLS.Level[];

  private constructor(type: MediaType, options: MediaOptions = {}, resourceUID: string) {
    super(resourceUID);

    options = {
      webkitPlaysinline: '',
      playsinline: '',
      preload: 'auto',
      crossorigin: 'anonymous',
      ...options,
    };

    this.ready = false;

    this.type = type;
    this.element = this.getElementByType(this.type);
    this.adapter = HTML5Adapter(this.element);

    // add element attributes
    (Object.keys(options) as Array<keyof typeof options>).forEach(key => {
      const val = options[key];
      if (val !== undefined) {
        this.element.setAttribute(kebabCase(key), val);
      }
    });

    this.hls = this.createHls();

    this.registerEvents();

    if (cacheRoot && this.element instanceof HTMLElement) {
      cacheRoot.appendChild(this.element);
    }
  }

  destroy() {
    this.emit(MediaEvents.Destroy);
    // this.adapter.destroy();
    this.unregisterEvents();
    this.removeAllListeners();
    this.hls.destroy();
    this.element.setAttribute('src', '');
    this.element.load();

    if (cacheRoot && this.element instanceof HTMLElement) {
      try {
        cacheRoot.removeChild(this.element);
      } catch (e) {
        console.log('Failed to remove element from cache: ', e.message);
      }
    }

    this.element?.remove();

    delete Media.resourceList[this.uid];
  }

  // action methods

  async play() {
    const result = await this.element.play();
    this.emit(MediaEvents.Change_Playing);
    return result;
  }

  pause() {
    this.element.pause();
    this.emit(MediaEvents.Change_Playing);
  }

  mute() {
    this.element.muted = true;
    this.emit(MediaEvents.Change_Mute, this.element.muted);
  }

  unmute() {
    this.element.muted = false;
    this.emit(MediaEvents.Change_Mute, this.element.muted);
  }

  // setter methods
  setResource({ src, levels }: { src: string; levels?: HLS.Level[] }) {
    this.customLevels = levels;

    if (this.isVideo()) {
      const isHlsCompatible = this.isHlsCompatible(src) && HLS.isSupported();
      const currentSrc = isHlsCompatible ? (this.hls as any).url : this.element.src;

      if (currentSrc === src) {
        if (this.ready) {
          this.handleReady();
        }

        return;
      }

      this.ready = false;

      this.emit(MediaEvents.Change_Resource, {
        prevSrc: currentSrc,
        nextSrc: src,
      });

      if (isHlsCompatible) {
        this.hls.destroy();
        this.hls = this.createHls();
        this.hls.loadSource(src);
      } else {
        this.pause();
        setTimeout(() => this.element.setAttribute('src', src));
      }
    } else {
      const currentSrc = this.element.src;
      if (currentSrc === src) {
        return;
      }

      this.emit(MediaEvents.Change_Resource, {
        prevSrc: currentSrc,
        nextSrc: src,
      });

      this.pause();
      setTimeout(() => this.element.setAttribute('src', src));
    }

    this.currentSrc = src;
  }

  setCurrentTime(time: number) {
    this.element.currentTime = time;
    this.emit(MediaEvents.Change_Time);
  }

  setVolume(volume: number) {
    this.element.volume = volume / 100;
    this.emit(MediaEvents.Change_Volume);
  }

  setLoop(value: boolean) {
    this.element.loop = value;
    this.emit(MediaEvents.Change_Loop);
  }

  setLevel(level: number) {
    if (this.isVideo()) {
      const isHlsCompatible = this.isHlsCompatible(this.currentSrc || '') && HLS.isSupported();

      if (isHlsCompatible) {
        this.hls.currentLevel = level;
      } else if (Array.isArray(this.customLevels)) {
        const src = this.customLevels[level]?.url;
        if (!src) return;

        this.pause();

        const currentTime = this.getCurrentTime();

        setTimeout(() => {
          this.element.setAttribute('src', src[0]);
          setTimeout(() => {
            this.setCurrentTime(currentTime);
            this.play().catch(() => console.log("Can't play after level change"));
          });
        });
      }

      this.emit(MediaEvents.Change_Level, level);
    }
  }

  // getter methods
  getCurrentTime() {
    return this.element.currentTime;
  }

  getDuration() {
    return this.element.duration;
  }

  getVolume() {
    return this.element.volume * 100;
  }

  isPaused() {
    return this.element.paused;
  }

  isMuted() {
    return this.element.muted;
  }

  isLoop() {
    return this.element.loop;
  }

  isVideo() {
    return this.type === MediaType.Video;
  }

  isReady() {
    return this.ready;
  }

  getLevels() {
    if (this.isVideo()) {
      const isHlsCompatible = this.isHlsCompatible(this.currentSrc || '') && HLS.isSupported();

      if (isHlsCompatible) {
        return this.hls.levels || [];
      }
      return this.customLevels || [];
    }

    return [];
  }

  getCurrentLevel() {
    if (this.isVideo()) {
      const isHlsCompatible = this.isHlsCompatible(this.currentSrc || '') && HLS.isSupported();

      if (isHlsCompatible) {
        return this.hls.currentLevel;
      }

      const currentSrc = isHlsCompatible ? (this.hls as any).url : this.element.src;

      if (Array.isArray(this.customLevels)) {
        return this.customLevels.findIndex(lvl => lvl.url[0] === currentSrc);
      }
      return -1;
    }
    return -1;
  }

  // private methods

  private createHls() {
    const hls = new HLS({
      levelLoadingTimeOut: 6000,
      levelLoadingMaxRetry: 3,
      levelLoadingRetryDelay: 1000,
      levelLoadingMaxRetryTimeout: 15000,

      fragLoadingTimeOut: 4000,
      fragLoadingMaxRetry: 3,
      fragLoadingRetryDelay: 1000,

      startLevel: 3,
    });
    hls.on(HLS.Events.MANIFEST_PARSED, this.handleReady);

    hls.on(HLS.Events.ERROR, (event, data) => {
      console.log('HLS.Events.ERROR: ', event, data);
      if (data.fatal) {
        switch (data.type) {
          case HLS.ErrorTypes.NETWORK_ERROR:
            // try to recover network error
            hls.startLoad();
            break;
          case HLS.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;
          default:
            // cannot recover
            hls.destroy();
            break;
        }
      } else if (
        data.details === 'internalException' &&
        (data.type === 'mediaError' || data.type === 'otherError') &&
        isMobile()
      ) {
        const level = last(this.getLevels());

        (hls as any).removeLevel(level?.level, level?.urlId);

        hls.startLoad();
      }
    });

    if (this.isVideo()) {
      hls.attachMedia(this.element);
    }

    return hls;
  }

  private registerEvents() {
    this.element.addEventListener('loadedmetadata', this.handleReady);
    // this.element.addEventListener('canplay', this.handleReady);
    this.element.addEventListener('error', this.handleError);

    this.element.addEventListener('playing', this.handlePlaying);
    this.element.addEventListener('pause', this.handlePause);
    this.element.addEventListener('ended', this.handleEnded);
    this.element.addEventListener('timeupdate', this.handleTimeupdate);
    this.element.addEventListener('progress', this.handleProgress);
  }

  private unregisterEvents() {
    this.element.removeEventListener('loadedmetadata', this.handleReady);
    // this.element.removeEventListener('canplay', this.handleReady);
    this.element.removeEventListener('error', this.handleError);

    this.element.removeEventListener('playing', this.handlePlaying);
    this.element.removeEventListener('pause', this.handlePause);
    this.element.removeEventListener('ended', this.handleEnded);
    this.element.removeEventListener('timeupdate', this.handleTimeupdate);
    this.element.removeEventListener('progress', this.handleProgress);
  }

  private getElementByType(type: MediaType) {
    switch (type) {
      case MediaType.Audio: {
        return document.createElement('audio') as HTMLAudioElement;
      }
      case MediaType.Video: {
        return document.createElement('video') as HTMLVideoElement;
      }
      case MediaType.Slideshow: {
        // @ts-ignore
        return new SlideshowMedia() as HTMLMediaElement;
      }
      default:
        throw new Error('Please provide correct type.');
    }
  }

  private isHlsCompatible(src: string) {
    const format = last<string>(src.split('.'));

    if (format !== 'mp4') {
      return true;
    }

    return false;
  }

  // private event handlers

  private handleReady = () => {
    if (this.element.readyState >= 1) {
      this.emit(MediaEvents.Ready);
      this.adapter.ready();
      this.handleTimeupdate();
      this.ready = true;
    } else {
      // if (this.isPaused()) {
      //   this.mute();
      //   this.play().finally(() => {
      //     this.pause();
      //     this.unmute();
      //   });
      // }

      console.log('Media not loaded.');
      // this.emit(MediaEvents.Error);
    }
  };

  private handleError = (evt: Event) => {
    console.log('Media error: ', evt);
    // this.emit(MediaEvents.Error, evt);
  };

  private handlePlaying = (evt: Event) => this.emit(MediaEvents.Play, evt);

  private handlePause = (evt: Event) => this.emit(MediaEvents.Pause, evt);

  private handleEnded = (evt: Event) => this.emit(MediaEvents.Ended, evt);

  private handleTimeupdate = () => {
    this.emit(MediaEvents.Timeupdate, {
      currentTime: this.element.currentTime,
      duration: this.element.duration,
    });
  };

  private handleProgress = () => {
    this.emit(MediaEvents.Buffered, {
      percent: this.element.buffered.length,
    });
  };

  //
  static getMediaFor(resourceUID: string, type: MediaType, options?: MediaOptions): Media {
    let media = Media.resourceList[resourceUID];

    if (media) {
      return media;
    }

    media = new Media(type, options, resourceUID);

    Media.resourceList[resourceUID] = media;

    return media;
  }

  static destroyMedia(media: Media | undefined) {
    if (media) {
      media.destroy();
    }
  }
}

export default Media;
